import { type ReactNode, useRef, useState, useCallback } from "react"
import { useDrag } from "@use-gesture/react"
import { cn } from "@/shared/utils/general/cn"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { useTouchPointer } from "@/shared/utils/platform/pointerKind"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import CalendarIcon from "@/shared/icons/totodoicons/stroke/calendar.svg?react"
import CheckIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import MoreIcon from "@/shared/icons/totodoicons/stroke/three-dots-horizontal.svg?react"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import { ObjectStatus } from "@/db/schema"

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

export type SwipeAction =
  | "schedule" // L→R short (<60%) — open date picker
  | "complete" // L→R full  (≥60%)
  | "more" // R→L short (<60%) — toggles multi-select
  | "contextMenu" // R→L full  (≥60%) — open context menu

export interface SwipeableTaskRowProps {
  children: ReactNode
  /** Called when a swipe action is committed */
  onAction?: (action: SwipeAction) => void
  /** Disable swipe (e.g. during DnD) */
  disabled?: boolean
  /** Border-radius class for visual grouping (applied to wrapper + bg-card) */
  borderRadiusClass?: string
  /**
   * Variant controls L→R action mapping:
   * - "task" (default): short = schedule (amber), long = complete (green)
   * - "section": short + long = complete (green) — no schedule for sections
   */
  variant?: "task" | "section"
  /** Inline style applied to the outermost wrapper */
  style?: React.CSSProperties
  /** Use muted reveal colors (semi-transparent white) — for use inside colored banners */
  muted?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Threshold percentages of row width */
const SHORT_THRESHOLD = 0.15 // 15% — triggers short action
const LONG_THRESHOLD = 0.60 // 60% — triggers long action
/** Minimum px before gesture activates */
const ACTIVATION_PX = 8
/** How fast a swipe must be to trigger at short threshold (px/ms) */
const VELOCITY_MIN = 0.3

// Colors for swipe reveal background
const COLOR_SCHEDULE = "#f59e0b" // amber-500 (L→R short: When)
const COLOR_COMPLETE = "#22c55e" // green-500 (L→R long: Complete)
const COLOR_SELECT = "var(--primary)" // theme primary (R→L short: Select)
const COLOR_CONTEXT = "#6b7280" // gray-500 (R→L long: Context menu)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Wraps a task row with horizontal swipe gestures (mobile only).
 *
 * - L→R short: Schedule / open date picker (amber)
 * - L→R long:  Complete task (green)
 * - R→L short: Toggle multi-select (primary)
 * - R→L long:  Open context menu (gray)
 *
 * On desktop, swipe is disabled — users use right-click context menu
 * and selection keyboard shortcuts instead.
 */
export function SwipeableTaskRow({
  children,
  onAction,
  disabled,
  borderRadiusClass,
  variant = "task",
  style,
  muted,
}: SwipeableTaskRowProps) {
  // Touch input gets the swipe gestures in any layout (iPad landscape); a mouse never does.
  const { isMobile: isMobileLayout } = useBreakpoint()
  const isTouchInput = useTouchPointer()
  const isMobile = isMobileLayout || isTouchInput
  const rowRef = useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = useState(0)
  const [bgColor, setBgColor] = useState("transparent")
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Track which haptic thresholds have fired during this swipe
  const hapticShortRef = useRef(false)
  const hapticLongRef = useRef(false)

  const getRowWidth = useCallback(
    () => rowRef.current?.offsetWidth ?? 300,
    [],
  )

  const computeAction = useCallback(
    (mx: number, rowWidth: number): SwipeAction | null => {
      const pct = Math.abs(mx) / rowWidth
      const isRight = mx > 0
      if (pct < SHORT_THRESHOLD) return null
      if (isRight) {
        // Section variant: L→R is always complete (no schedule)
        if (variant === "section") return "complete"
        return pct >= LONG_THRESHOLD ? "complete" : "schedule"
      }
      return pct >= LONG_THRESHOLD ? "contextMenu" : "more"
    },
    [variant],
  )

  const computeBgColor = useCallback(
    (mx: number, rowWidth: number): string => {
      const pct = Math.abs(mx) / rowWidth
      if (pct < SHORT_THRESHOLD) return "transparent"
      if (muted) return "rgba(255,255,255,0.18)"
      const isRight = mx > 0
      if (isRight) {
        // Section variant: always green (complete)
        if (variant === "section") return COLOR_COMPLETE
        return pct >= LONG_THRESHOLD ? COLOR_COMPLETE : COLOR_SCHEDULE
      }
      return pct >= LONG_THRESHOLD ? COLOR_CONTEXT : COLOR_SELECT
    },
    [variant, muted],
  )

  const bind = useDrag(
    ({
      movement: [mx],
      velocity: [vx],
      last,
      cancel,
      memo,
    }) => {
      // Disabled or desktop → no swipe
      if (disabled || !isMobile) {
        cancel?.()
        return memo
      }

      const rowWidth = getRowWidth()
      const pct = Math.abs(mx) / rowWidth

      // ── During drag ──────────────────────────────────────────────────
      if (!last) {
        // Haptic feedback at thresholds
        if (pct >= SHORT_THRESHOLD && !hapticShortRef.current) {
          hapticShortRef.current = true
          hapticLight()
        }
        if (pct >= LONG_THRESHOLD && !hapticLongRef.current) {
          hapticLongRef.current = true
          hapticMedium()
        }

        // Apply rubber-band dampening past long threshold
        let dampened = mx
        if (pct > LONG_THRESHOLD) {
          const excess = Math.abs(mx) - rowWidth * LONG_THRESHOLD
          dampened =
            Math.sign(mx) * (rowWidth * LONG_THRESHOLD + excess * 0.3)
        }

        setOffsetX(dampened)
        setBgColor(computeBgColor(mx, rowWidth))
        return memo
      }

      // ── Release ────────────────────────────────────────────────────────
      // Reset haptic trackers
      hapticShortRef.current = false
      hapticLongRef.current = false

      // Determine action: either by threshold or by velocity
      let action = computeAction(mx, rowWidth)

      // Velocity boost: fast swipe past halfway to short threshold counts
      if (!action && vx > VELOCITY_MIN && pct > SHORT_THRESHOLD * 0.5) {
        action = mx > 0 ? "schedule" : "more"
      }

      if (action && onAction) {
        hapticMedium()
        onAction(action)

        // Prevent the browser's synthetic click event that fires after
        // touchend from reaching ancestor onClick handlers. Without this,
        // the parent div's onClick (e.g. handleSectionClick) would fire
        // and double-toggle the selection that onAction just set.
        const stop = (e: Event) => { e.stopPropagation(); e.preventDefault() }
        document.addEventListener("click", stop, { capture: true, once: true })
        setTimeout(() => document.removeEventListener("click", stop, { capture: true }), 400)
      }

      // Spring back
      setIsTransitioning(true)
      setOffsetX(0)
      setBgColor("transparent")
      requestAnimationFrame(() => {
        setTimeout(() => setIsTransitioning(false), 300)
      })

      return memo
    },
    {
      axis: "x",
      filterTaps: true,
      pointer: { touch: true },
      threshold: ACTIVATION_PX,
    },
  )

  // Desktop: simple wrapper, no gestures
  if (!isMobile) {
    return (
      <div className={cn("relative overflow-hidden", borderRadiusClass)} style={style}>
        <div className={cn("relative z-10 bg-card", borderRadiusClass)}>
          {children}
        </div>
      </div>
    )
  }

  // During active swipe, use full rounded corners so the reveal and card
  // look consistent regardless of position in the group (first/middle/last).
  const isSwiping = offsetX !== 0
  const swipeRadius = isSwiping ? "rounded-lg" : borderRadiusClass

  // Mobile: swipeable wrapper
  return (
    <div
      ref={rowRef}
      className={cn("relative overflow-hidden", swipeRadius)}
      style={{ touchAction: "pan-y", ...style }}
    >
      {/* Reveal background — shows action color during swipe */}
      <div
        className={cn(
          "absolute inset-0 flex items-center",
          swipeRadius,
          offsetX > 0 ? "justify-start pl-4" : "justify-end pr-4",
        )}
        style={{ backgroundColor: bgColor }}
        aria-hidden
      >
        {/* Action icon hint — Pikaicons + TimelineIcon */}
        {Math.abs(offsetX) > getRowWidth() * SHORT_THRESHOLD && (
          <span className="flex items-center justify-center text-white">
            {offsetX > 0 ? (
              variant === "section" || Math.abs(offsetX) / getRowWidth() >= LONG_THRESHOLD ? (
                <CheckIcon width={22} height={22} />
              ) : (
                <CalendarIcon width={22} height={22} />
              )
            ) : (
              Math.abs(offsetX) / getRowWidth() >= LONG_THRESHOLD ? (
                <MoreIcon width={22} height={22} />
              ) : (
                <DynamicIcon
                  type="project"
                  percentage={100}
                  unfinished={0}
                  status={ObjectStatus.Unfinished}
                  size="small"
                  color="white"
                />
              )
            )}
          </span>
        )}
      </div>

      {/* Foreground card — slides with swipe */}
      <div
        {...bind()}
        className={cn("relative z-10 bg-card", swipeRadius)}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isTransitioning
            ? "transform 300ms cubic-bezier(0.2, 0, 0, 1)"
            : "none",
        }}
        {...(isSwiping ? { "data-swiping": "" } : {})}
      >
        {children}
      </div>
    </div>
  )
}
